import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

import { closeIcon as CloseIcon } from "assets/icons";
import CalendarFrame from "../../components/MonthCalendar/CalendarFrame";
import { useNotify } from "../../context/NotificationContext";
import { fetchStudentCalendar } from "../../services/studentPortalService/studentPortalService";
import type {
  StudentCalendarDay as StudentCalendarDayData,
  StudentCalendarEvent,
} from "../../services/studentPortalService/studentPortalService.types";
import { logger } from "../../utils/logger";
import {
  PageLead,
  PageTitle,
} from "./studentPortal.styles";
import {
  CalendarDayDate,
  CalendarDayMonth,
  CalendarDayNumber,
  CalendarDaySlot,
  CalendarDayState,
  CalendarDayTop,
  CalendarDrawerMotion,
  CalendarDrawerPanel,
  CalendarFooterHint,
  CalendarFooterMain,
  CalendarHeaderMetrics,
  CalendarLoading,
  CalendarMainMotion,
  CalendarMetric,
  CalendarPageHeader,
  CalendarPageShell,
  CalendarTodayBadge,
  CalendarWorkspace,
  CurrentMonthButton,
  DayEntries,
  DayEvent,
  DayEventCourse as DayEventTitle,
  DayEventDescription as DayEventCourseMeta,
  DayEventTime,
  DayEventTop,
  DrawerCloseButton,
  DrawerContent,
  DrawerCourseButton,
  DrawerEventCard,
  DrawerEventCourse as DrawerEventTitle,
  DrawerEventDescription as DrawerEventCourseMeta,
  DrawerEventHeader,
  DrawerEventList,
  DrawerEventTime,
  DrawerEyebrow,
  DrawerHeader,
  DrawerHeaderCopy,
  DrawerSubtitle,
  DrawerTitle,
  MobileDrawerBackdrop,
  MonthControls,
  MonthDisplay,
  MonthNavButton,
  MoreEvents,
  StudentCalendarDay,
  VisuallyHidden,
} from "./StudentCalendarPage.styles";

const WEEKDAYS = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];
const WEEKDAYS_FULL = ["Понедельник", "Вторник", "Среда", "Четверг", "Пятница", "Суббота", "Воскресенье"];
const MONTHS = [
  "Январь",
  "Февраль",
  "Март",
  "Апрель",
  "Май",
  "Июнь",
  "Июль",
  "Август",
  "Сентябрь",
  "Октябрь",
  "Ноябрь",
  "Декабрь",
];
const MONTHS_GENITIVE = [
  "января",
  "февраля",
  "марта",
  "апреля",
  "мая",
  "июня",
  "июля",
  "августа",
  "сентября",
  "октября",
  "ноября",
  "декабря",
];
const MONTHS_SHORT = ["янв", "фев", "мар", "апр", "май", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"];
const DESKTOP_DRAWER_WIDTH = 408;
const DRAWER_BREAKPOINT = 900;

type CalendarCellDescriptor = {
  date: string;
  day: number;
  monthLabel: string;
  adjacent: boolean;
};

const toIsoDate = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

const parseIsoDate = (value: string) => {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day);
};

const buildCalendarDays = (year: number, month: number): CalendarCellDescriptor[] => {
  const first = new Date(year, month - 1, 1);
  const mondayOffset = (first.getDay() + 6) % 7;
  const start = new Date(year, month - 1, 1 - mondayOffset);

  return Array.from({ length: 42 }, (_, index) => {
    const current = new Date(start.getFullYear(), start.getMonth(), start.getDate() + index);
    return {
      date: toIsoDate(current),
      day: current.getDate(),
      monthLabel: MONTHS_SHORT[current.getMonth()],
      adjacent: current.getMonth() !== month - 1,
    };
  });
};

const eventCountLabel = (count: number) => {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) return `${count} событие`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return `${count} события`;
  return `${count} событий`;
};

const formatSelectedDate = (value: string) => {
  const date = parseIsoDate(value);
  return `${date.getDate()} ${MONTHS_GENITIVE[date.getMonth()]}`;
};

const formatSelectedWeekday = (value: string) => {
  const date = parseIsoDate(value);
  const weekdayIndex = (date.getDay() + 6) % 7;
  return WEEKDAYS_FULL[weekdayIndex];
};

const StudentCalendarPage: React.FC = () => {
  const navigate = useNavigate();
  const now = new Date();
  const [cursor, setCursor] = useState(() => new Date(now.getFullYear(), now.getMonth(), 1));
  const [days, setDays] = useState<StudentCalendarDayData[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [isMobileDrawer, setIsMobileDrawer] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= DRAWER_BREAKPOINT,
  );
  const toast = useNotify();

  const year = cursor.getFullYear();
  const month = cursor.getMonth() + 1;
  const monthTitle = `${MONTHS[month - 1]} ${year}`;
  const todayIso = toIsoDate(now);

  useEffect(() => {
    const updateDrawerMode = () => setIsMobileDrawer(window.innerWidth <= DRAWER_BREAKPOINT);
    window.addEventListener("resize", updateDrawerMode);
    return () => window.removeEventListener("resize", updateDrawerMode);
  }, []);

  useEffect(() => {
    if (!selectedDate) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setSelectedDate(null);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [selectedDate]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setDays([]);

    fetchStudentCalendar(year, month)
      .then((data) => {
        if (active) setDays(data.calendar);
      })
      .catch((error) => {
        logger.error("Ошибка загрузки календаря ученика:", error);
        toast.show({ severity: "error", summary: "Ошибка", detail: "Не удалось загрузить календарь", life: 3000 });
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [month, toast, year]);

  const eventsByDate = useMemo(() => {
    const map = new Map<string, StudentCalendarEvent[]>();
    days.forEach((day) => map.set(day.date, day.events));
    return map;
  }, [days]);

  const calendarDays = useMemo(() => buildCalendarDays(year, month), [month, year]);
  const totalEvents = useMemo(() => days.reduce((sum, day) => sum + day.events.length, 0), [days]);
  const activeDays = useMemo(() => days.filter((day) => day.events.length > 0).length, [days]);
  const selectedEvents = selectedDate ? eventsByDate.get(selectedDate) || [] : [];

  const shiftMonth = (delta: number) => {
    setSelectedDate(null);
    setCursor((current) => new Date(current.getFullYear(), current.getMonth() + delta, 1));
  };

  const resetToCurrentMonth = () => {
    const current = new Date();
    setSelectedDate(null);
    setCursor(new Date(current.getFullYear(), current.getMonth(), 1));
  };

  const selectDay = (date: string, events: StudentCalendarEvent[]) => {
    if (events.length > 0) setSelectedDate(date);
  };

  return (
    <CalendarPageShell>
      <CalendarPageHeader>
        <div>
          <PageTitle>Календарь</PageTitle>
          <PageLead>События по всем курсам, к которым у вас сейчас открыт доступ.</PageLead>
        </div>
      </CalendarPageHeader>

      <CalendarWorkspace>
        <CalendarMainMotion layout transition={{ duration: 0.34, ease: [0.16, 1, 0.3, 1] }}>
          <CalendarFrame
            compact
            title={monthTitle}
            weekdays={WEEKDAYS}
            meta={
              <CalendarHeaderMetrics>
                {loading ? (
                  <CalendarLoading>Загружаем события…</CalendarLoading>
                ) : (
                  <>
                    <CalendarMetric>Событий <strong>{totalEvents}</strong></CalendarMetric>
                    <CalendarMetric>Дней с событиями <strong>{activeDays}</strong></CalendarMetric>
                  </>
                )}
              </CalendarHeaderMetrics>
            }
            footer={
              <CalendarFooterMain>
                <MonthControls>
                  <MonthNavButton variant="secondary" onClick={() => shiftMonth(-1)}>
                    <span aria-hidden="true">‹</span>
                    <VisuallyHidden>Предыдущий месяц</VisuallyHidden>
                  </MonthNavButton>
                  <MonthDisplay>{monthTitle}</MonthDisplay>
                  <MonthNavButton variant="secondary" onClick={() => shiftMonth(1)}>
                    <span aria-hidden="true">›</span>
                    <VisuallyHidden>Следующий месяц</VisuallyHidden>
                  </MonthNavButton>
                  <CurrentMonthButton variant="secondary" onClick={resetToCurrentMonth}>
                    Текущий
                  </CurrentMonthButton>
                </MonthControls>
                <CalendarFooterHint>Нажмите на день с событиями, чтобы посмотреть детали</CalendarFooterHint>
              </CalendarFooterMain>
            }
          >
            {calendarDays.map((day) => {
              const events = day.adjacent ? [] : eventsByDate.get(day.date) || [];
              const isToday = !day.adjacent && day.date === todayIso;
              const hasEvents = events.length > 0;
              const isSelected = !day.adjacent && day.date === selectedDate;

              return (
                <CalendarDaySlot key={day.date} data-calendar-day={day.date}>
                  <StudentCalendarDay
                    type="button"
                    disabled={!hasEvents}
                    aria-pressed={isSelected}
                    aria-label={
                      hasEvents
                        ? `${day.day} ${MONTHS_GENITIVE[month - 1]}, ${eventCountLabel(events.length)}`
                        : undefined
                    }
                    $adjacent={day.adjacent}
                    $today={isToday}
                    $hasEvents={hasEvents}
                    $selected={isSelected}
                    onClick={() => selectDay(day.date, events)}
                  >
                    <CalendarDayTop>
                      <CalendarDayDate>
                        <CalendarDayNumber $adjacent={day.adjacent}>{day.day}</CalendarDayNumber>
                        <CalendarDayMonth>{day.monthLabel}</CalendarDayMonth>
                      </CalendarDayDate>
                      {!day.adjacent && isToday ? (
                        <CalendarTodayBadge>сегодня</CalendarTodayBadge>
                      ) : !day.adjacent && hasEvents ? (
                        <CalendarDayState>{eventCountLabel(events.length)}</CalendarDayState>
                      ) : null}
                    </CalendarDayTop>

                    {!day.adjacent ? (
                      <DayEntries>
                        {events.slice(0, 2).map((event, index) => (
                          <DayEvent key={`${day.date}:${event.course_id}:${event.time || "all-day"}:${index}`}>
                            <DayEventTop>
                              {event.time ? <DayEventTime>{event.time}</DayEventTime> : null}
                              <DayEventTitle title={event.description}>{event.description}</DayEventTitle>
                            </DayEventTop>
                            <DayEventCourseMeta title={event.course}>{event.course}</DayEventCourseMeta>
                          </DayEvent>
                        ))}
                        {events.length > 2 ? <MoreEvents>Ещё {events.length - 2}</MoreEvents> : null}
                      </DayEntries>
                    ) : null}
                  </StudentCalendarDay>
                </CalendarDaySlot>
              );
            })}
          </CalendarFrame>
        </CalendarMainMotion>

        {isMobileDrawer && selectedDate ? (
          <MobileDrawerBackdrop
            type="button"
            aria-label="Закрыть события дня"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2 }}
            onClick={() => setSelectedDate(null)}
          />
        ) : null}

        <CalendarDrawerMotion
          data-testid="student-calendar-drawer"
          aria-hidden={!selectedDate}
          style={{
            pointerEvents: selectedDate ? "auto" : "none",
            width: isMobileDrawer ? undefined : selectedDate ? DESKTOP_DRAWER_WIDTH : 0,
            opacity: selectedDate ? 1 : 0,
            transform: isMobileDrawer
              ? `translateY(${selectedDate ? 0 : 32}px)`
              : undefined,
            transition: isMobileDrawer
              ? "opacity 0.2s ease, transform 0.34s cubic-bezier(0.16, 1, 0.3, 1)"
              : "width 0.34s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.2s ease",
          }}
        >
          {selectedDate ? (
            <CalendarDrawerPanel aria-label={`События: ${formatSelectedDate(selectedDate)}`}>
              <DrawerHeader>
                <DrawerHeaderCopy>
                  <DrawerEyebrow>События дня</DrawerEyebrow>
                  <DrawerTitle>{formatSelectedDate(selectedDate)}</DrawerTitle>
                  <DrawerSubtitle>
                    {formatSelectedWeekday(selectedDate)} · {eventCountLabel(selectedEvents.length)}
                  </DrawerSubtitle>
                </DrawerHeaderCopy>
                <DrawerCloseButton type="button" aria-label="Закрыть события дня" onClick={() => setSelectedDate(null)}>
                  <CloseIcon aria-hidden="true" />
                </DrawerCloseButton>
              </DrawerHeader>

              <DrawerContent
                as={motion.div}
                key={selectedDate}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.18, ease: "easeOut" }}
              >
                <DrawerEventList>
                  {selectedEvents.map((event, index) => (
                    <DrawerEventCard key={`${selectedDate}:${event.course_id}:${event.time || "all-day"}:${index}`}>
                      <DrawerEventHeader>
                        <DrawerEventTime>{event.time || "Без времени"}</DrawerEventTime>
                      </DrawerEventHeader>
                      <DrawerEventTitle
                        title={event.description}
                        style={{ whiteSpace: "normal", overflow: "visible", textOverflow: "clip" }}
                      >
                        {event.description}
                      </DrawerEventTitle>
                      <DrawerEventCourseMeta>Курс: {event.course}</DrawerEventCourseMeta>
                      <DrawerCourseButton
                        variant="secondary"
                        size="sm"
                        onClick={() => navigate(`/student/courses/${event.course_id}`)}
                      >
                        Перейти к курсу
                      </DrawerCourseButton>
                    </DrawerEventCard>
                  ))}
                </DrawerEventList>
              </DrawerContent>
            </CalendarDrawerPanel>
          ) : null}
        </CalendarDrawerMotion>
      </CalendarWorkspace>
    </CalendarPageShell>
  );
};

export default StudentCalendarPage;